import React, { useCallback, useEffect, useState, useMemo } from 'react'

import { useSelector, useDispatch } from 'react-redux'
import { Typography, Grid, Box, Paper } from '@mui/material'
import Button from 'components/Common/Button'
import BusinessList from 'components/Monitor/BusinessList'
import { styled } from '@mui/material/styles'

import { makeSubscribable } from 'helpers'

import api from 'api'

import MuiAlert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'

import { fetchMonitoredBusiness, fetchClaimedBusinesses } from 'store/actions/businessListingActions'
import ThreeDotsLoadingSpinner from 'components/Common/LoadingSpinner/threeDots'
import BusinessCardSquare from 'components/Monitor/BusinessCardSquare'
import { useNavigate } from 'react-router-dom'

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
})

const YourBusinessBox = styled(Paper)(({ theme }) => ({
  width: '300px',
  height: '270px',
  boxShadow: '4px 4px 15px rgba(10, 120, 205, 0.08)',
  borderRadius: '10px',
}))

const FollowedBusiness = () => {
  const [toast, setToast] = useState({
    open: false,
    message: '',
  })
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [businessData, setBusinessData] = useState({})

  const user = useSelector(state => state.userReducer.user)
  const monitorCompanies = useSelector(state => state.businessReducer.monitoredCompanies.companies)
  const monitorCompaniesLoaded = useSelector(state => state.businessReducer.monitorCompaniesLoaded)
  const claimedBusinesses = useSelector(state => state.businessReducer.claimedBusinesses)
  const claimedBusinessesLoaded = useSelector(state => state.businessReducer.claimedBusinessesLoaded)

  const [loading, setLoading] = useState(true)

  const [models, setModels] = useState({
    monitored: [],
    claimed: null,
  })

  useEffect(() => {
    if (!user) return
    fetchAndSetData()
  }, [user])

  useEffect(() => {
    fetchMonitoredBusiness(dispatch)
    fetchClaimedBusinesses(dispatch)
  }, [dispatch])

  const fetchAndSetData = async () => {
    const data = await api.getDashboardInfo()

    if (data.business_info?.claimed_business_details) {
      setBusinessData(data.business_info?.claimed_business_details)
      return
    }
  }

  const onCardRemoveClick = useCallback(
    model => {
      //TODO: Check api for removing monitored business
      const nextModels = models.monitored.filter(item => item.id !== model.id)
      setModels(state => ({ ...state, monitored: nextModels }))

      const promise = api.updateMonitorIds(
        monitorCompanies.map(company => company.ext_id),
        model,
      )

      makeSubscribable(promise).subscribe(data => {
        setToast({ open: true, message: 'Successfully removed!' })
        dispatch({ type: 'FETCHBMONITEREDCOMPANIES', payload: data })
      })
    },
    [models, monitorCompanies, dispatch],
  )

  const filteredMonitored = useMemo(() => {
    if (monitorCompaniesLoaded && monitorCompanies && user && user.businessIds) {
      return monitorCompanies.filter(monitored => {
        const hasInClaimed = user.businessIds?.includes(monitored.ext_id)
        return !hasInClaimed
      })
    }
    return monitorCompanies
  }, [monitorCompanies, monitorCompaniesLoaded, user])

  useEffect(() => {
    if (claimedBusinessesLoaded && monitorCompaniesLoaded) {
      setLoading(false)
    }
  }, [claimedBusinessesLoaded, monitorCompaniesLoaded])

  useEffect(() => {
    dispatch({ type: 'searchReset' })
  }, [dispatch])

  const closeToast = () => {
    setToast({ open: false, message: '' })
  }

  if (user === null) return null

  const userBusiness = (
    <Box width="100%">
      <Grid item xs={12} style={{ paddingBottom: 0 }}>
        <Box px={2} mb={2} marginLeft="16px" color="#25245F">
          <Typography variant="h6" marginTop="70px">
            {user.businessIds && user.businessIds.length > 1 ? 'Your Businesses' : 'Your Business'}
          </Typography>
        </Box>
      </Grid>
      <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column">
        {/* {!kyb && (
          <CallToAction
            type={CTA_TYPES.PRIMARY}
            route={routes.find}
            cta="Go To Find"
            title="To show your business information here, search for your business and verify it."
          />
        )} */}
        {!businessData?.isClaimed && claimedBusinesses.length < 1 && (
          <>
            <Box px={2} width={'100%'} marginBottom="20px" color="#25245f">
              <YourBusinessBox>
                <Box display="flex" justifyContent="center" alignItems="center">
                  <Typography variant="body1M" align="center" color="secondary" margin="27% 20% 7% 20%">
                    Be in control of your public information
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="center">
                  <Button style={{ width: '230px' }} onClick={() => navigate('/find')}>
                    Claim Your Business
                  </Button>
                </Box>
              </YourBusinessBox>
            </Box>
          </>
        )}
        {claimedBusinesses && (
          <>
            {claimedBusinesses.map(business => (
              <Box
                px={2}
                width={'100%'}
                marginBottom="20px"
                color="#25245f"
                key={Math.random()}
                // sx={{ cursor: 'pointer' }}
              >
                <BusinessCardSquare model={business} accountModel={user} isMonitored={false} />
              </Box>
            ))}
          </>
        )}
      </Box>
    </Box>
  )

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <ThreeDotsLoadingSpinner width="60px" />
      </Box>
    )
  }
  //       padding: theme.spacing(3.5, 3, 0, 3),
  return (
    <Box sx={{ maxWidth: 1138, margin: '0 auto' }}>
      <Grid container spacing={4}>
        {userBusiness}
        <BusinessList models={filteredMonitored} onCardRemoveClick={onCardRemoveClick} isMonitored={true} />
      </Grid>
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={toast.open}
        autoHideDuration={5000}
        onClose={closeToast}
      >
        <Alert role="alert" onClose={closeToast} sx={{ width: '100%', fontSize: '14px' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default FollowedBusiness
